// Adapter for Segment Analytics with dependency injection
// Makes testing easier by allowing mock implementations

import { AnalyticsBrowser, Context, ID } from '@segment/analytics-next';

export interface SegmentAnalytics {
  track(eventName: string, properties?: Record<string, any>): Promise<Context>;
  page(pageName: string, properties?: Record<string, any>): Promise<Context>;
  identify(userId: string, traits?: Record<string, any>): Promise<Context>;
  setAnonymousId(id: string): Promise<ID>;
  ready(): Promise<void>;
}

export interface SegmentConfig {
  writeKey: string;
  debug?: boolean;
}

export class SegmentAdapter implements SegmentAnalytics {
  private analytics: AnalyticsBrowser;

  constructor(config: SegmentConfig) {
    this.analytics = AnalyticsBrowser.load({ writeKey: config.writeKey }, { obfuscate: true });
  }

  async ready(): Promise<void> {
    await this.analytics.ready();
  }

  async track(eventName: string, properties?: Record<string, any>): Promise<Context> {
    return await this.analytics.track(eventName, properties);
  }

  async page(pageName: string, properties?: Record<string, any>): Promise<Context> {
    return await this.analytics.page(pageName, properties);
  }

  async identify(userId: string, traits?: Record<string, any>): Promise<Context> {
    return await this.analytics.identify(userId, traits);
  }

  async setAnonymousId(id: string): Promise<ID> {
    return await this.analytics.setAnonymousId(id);
  }
}

export class MockSegmentAdapter implements SegmentAnalytics {
  public events: Array<{
    type: string;
    eventName?: string;
    properties?: Record<string, any>;
    userId?: string;
    traits?: Record<string, any>;
    anonymousId?: string;
  }> = [];

  private shouldFail: boolean = false;
  private readonly readyDelay: number = 0;
  public readonly writeKey: string = '';

  constructor(config?: { shouldFail?: boolean; readyDelay?: number; writeKey?: string }) {
    this.shouldFail = config?.shouldFail ?? false;
    this.readyDelay = config?.readyDelay ?? 0;
    this.writeKey = config?.writeKey ?? '';
  }

  async ready(): Promise<void> {
    if (this.readyDelay > 0) {
      await new Promise((resolve) => setTimeout(resolve, this.readyDelay));
    }

    if (this.shouldFail) {
      throw new Error('Mock Segment initialization failed');
    }
  }

  async track(eventName: string, properties?: Record<string, any>): Promise<Context> {
    if (this.shouldFail) {
      throw new Error('Mock track failed');
    }

    this.events.push({
      type: 'track',
      eventName,
      properties,
    });

    return new Context({ type: 'track' });
  }

  async page(pageName: string, properties?: Record<string, any>): Promise<Context> {
    if (this.shouldFail) {
      throw new Error('Mock page failed');
    }

    this.events.push({
      type: 'page',
      eventName: pageName,
      properties,
    });

    return new Context({ type: 'page' });
  }

  async identify(userId: string, traits?: Record<string, any>): Promise<Context> {
    if (this.shouldFail) {
      throw new Error('Mock identify failed');
    }

    this.events.push({
      type: 'identify',
      userId,
      traits,
    });

    return new Context({ type: 'identify' });
  }

  async setAnonymousId(id: string): Promise<ID> {
    if (this.shouldFail) {
      throw new Error('Mock setAnonymousId failed');
    }

    this.events.push({
      type: 'setAnonymousId',
      anonymousId: id,
    });

    return '123';
  }

  // Test utilities
  getTrackedEvents(): typeof this.events {
    return [...this.events];
  }

  clearEvents(): void {
    this.events = [];
  }

  setFailureMode(shouldFail: boolean): void {
    this.shouldFail = shouldFail;
  }
}
